'use client';

import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import { Country, ObligationRulesCategory } from '@commenda/prisma';
import { cn, ComplianceModel, NonTimezoneSensitiveDate } from '@commenda/utils';
import { useMemo, useState } from 'react';
import { ComplianceFrequencyEnumToText } from '../../../Corporation/Monitor/Filings/types';
import { FormCard } from '../../../form-builder/components/new-incorp-layout';
import { FilingCategoryBadge } from '../../../molecules/filings/FilingCategoryBadge';
import { idOfPendingDecision } from './confirmationRows';
import {
  type PendingDecision,
  type PreviewRow,
} from './confirmationRows.types';

const ALL_CATEGORIES = '__all__';

/** Format an ISO date (YYYY-MM-DD…) as "MMM DD, YYYY", timezone-safe (matches the filings UI). */
const formatDueDate = (dueDate: string | null): string | null =>
  dueDate
    ? NonTimezoneSensitiveDate.fromISO8601String(dueDate).format('MMM DD, YYYY')
    : null;

/** A single rounded-full category filter pill, matching the Figma (separated pills, not a segmented control). */
const CategoryChip = ({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex h-7 items-center rounded-full border border-border-primary px-lg txt-extra-small-medium',
      isActive
        ? 'bg-bg-secondary text-text-primary'
        : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary'
    )}
  >
    {label}
  </button>
);

const ConfirmationCard = ({
  row,
  decision,
  isCommitting,
  country,
  onDecide,
  onUndo,
}: {
  row: PreviewRow;
  decision: PendingDecision | undefined;
  isCommitting: boolean;
  country?: Country;
  onDecide: (decisionKey: string, decision: PendingDecision) => void;
  onUndo: (decisionKey: string) => void;
}) => {
  const decisionKey = idOfPendingDecision(row);
  const frequencyLabel = row.frequency
    ? ComplianceFrequencyEnumToText[row.frequency]
    : null;
  const dueLabel = formatDueDate(row.dueDate);
  const textMeta = [frequencyLabel, dueLabel ? `Due ${dueLabel}` : null].filter(
    (part): part is string => Boolean(part)
  );

  return (
    <FormCard>
      <div className="flex w-full flex-col gap-md">
        <p className="txt-medium-bold text-text-primary">{row.title}</p>
        {(row.category || textMeta.length > 0) && (
          <div className="flex flex-wrap items-center gap-md">
            {row.category && country && (
              <FilingCategoryBadge category={row.category} country={country} />
            )}
            {textMeta.length > 0 && (
              <span className="txt-extra-small-regular text-text-secondary">
                {textMeta.join(' · ')}
              </span>
            )}
          </div>
        )}
      </div>

      {decision === 'CONFIRM' ? (
        <div className="flex w-full items-center justify-between rounded-md bg-bg-success p-lg">
          <p className="txt-small-bold text-text-success">
            Added to your calendar
          </p>
          <Button
            variant="brand-link"
            size="sm"
            disabled={isCommitting}
            onClick={() => onUndo(decisionKey)}
          >
            Undo
          </Button>
        </div>
      ) : decision === 'DENY' ? (
        <div className="flex w-full items-center justify-between rounded-md bg-bg-secondary p-lg">
          <p className="txt-small-bold text-text-disabled">
            Excluded from your calendar
          </p>
          <Button
            variant="brand-link"
            size="sm"
            disabled={isCommitting}
            onClick={() => onUndo(decisionKey)}
          >
            Undo
          </Button>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-xl rounded-md border border-border-primary bg-bg-primary p-lg">
          <div className="flex flex-col gap-xs">
            <p className="txt-small-bold text-text-primary">
              Does this apply to you?
            </p>
            {row.ruleDescription && (
              <p className="txt-small-regular text-text-primary">
                {row.ruleDescription}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-md">
            <Button
              variant="secondary"
              size="sm"
              disabled={isCommitting}
              leftIcon={<Icons.Check className="size-4" />}
              onClick={() => onDecide(decisionKey, 'CONFIRM')}
            >
              Yes, this applies
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={isCommitting}
              leftIcon={<Icons.X className="size-4" />}
              onClick={() => onDecide(decisionKey, 'DENY')}
            >
              No, doesn&apos;t apply
            </Button>
          </div>
        </div>
      )}
    </FormCard>
  );
};

export const PartConfirmation = ({
  rows,
  pending,
  isLoading,
  isCommitting,
  country,
  onDecide,
  onUndo,
}: {
  rows: PreviewRow[];
  pending: Record<string, PendingDecision>;
  isLoading: boolean;
  isCommitting: boolean;
  country?: Country;
  onDecide: (rowId: string, decision: PendingDecision) => void;
  onUndo: (rowId: string) => void;
}) => {
  const [selectedCategory, setSelectedCategory] =
    useState<string>(ALL_CATEGORIES);

  // Category filter options are derived from the categories actually present in the loaded filings.
  const presentCategories = useMemo(() => {
    const seen: ObligationRulesCategory[] = [];
    rows.forEach((row) => {
      if (row.category && !seen.includes(row.category)) seen.push(row.category);
    });
    return seen;
  }, [rows]);

  const visibleRows =
    selectedCategory === ALL_CATEGORIES
      ? rows
      : rows.filter((row) => row.category === selectedCategory);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3">
        <LoadingIndicator size={24} />
        <p className="txt-small-regular text-text-tertiary">
          Loading the filings that need your confirmation…
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="txt-small-regular text-text-tertiary">
        There are no filings that need your confirmation right now.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3xl">
      {presentCategories.length > 1 && (
        <div className="flex flex-wrap items-center gap-md">
          <CategoryChip
            label="All"
            isActive={selectedCategory === ALL_CATEGORIES}
            onClick={() => setSelectedCategory(ALL_CATEGORIES)}
          />
          {presentCategories.map((category) => (
            <CategoryChip
              key={category}
              label={ComplianceModel.getFormattedComplianceCategory(
                category,
                country
              )}
              isActive={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2xl">
        {visibleRows.map((row) => (
          <ConfirmationCard
            key={row.id}
            row={row}
            decision={pending[idOfPendingDecision(row)]}
            isCommitting={isCommitting}
            country={country}
            onDecide={onDecide}
            onUndo={onUndo}
          />
        ))}
      </div>
    </div>
  );
};
